if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          counter: this.querySelector('[data-gallery-counter]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.touchStart = null;
        this.isEditorialGrid = this.dataset.editorialGrid === 'true';

        if (!this.elements.viewer) return;

        if (this.isEditorialGrid) {
          this.showAllEditorialMedia();
          return;
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || !mediaToSwitch.dataset.target) return;
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.elements.viewer.addEventListener('pointerdown', (event) => this.onPointerStart(event));
        this.elements.viewer.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.elements.viewer.addEventListener('pointerup', (event) => this.onPointerEnd(event));
        this.elements.viewer.addEventListener('dragstart', (event) => event.preventDefault());

        const initial =
          this.elements.viewer.querySelector('.product__media-list > .product__media-item.is-active[data-media-id]') ||
          this.elements.viewer.querySelector('.product__media-list > .product__media-item[data-media-id]');
        if (initial) this.setActiveMedia(initial.dataset.mediaId, false, { preventScroll: true, announce: false });

        const desktopLayout = this.dataset.desktopLayout || '';
        if (desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      showAllEditorialMedia() {
        if (!this.elements.viewer) return;
        this.elements.viewer.querySelectorAll('.product__media-list > .product__media-item[data-media-id]').forEach((item) => {
          item.hidden = false;
          item.removeAttribute('aria-hidden');
        });
      }

      onSlideChanged(event) {
        const currentElement = event?.detail?.currentElement;
        const mediaId = currentElement?.dataset?.mediaId;
        if (!mediaId || !this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!thumbnail) return;
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        if (!this.elements.viewer || !mediaId) return;

        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-list > .product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-list > .product__media-item[data-media-id]');
        if (!activeMedia) return;

        if (this.isEditorialGrid) {
          this.elements.viewer.querySelectorAll('.product__media-list > .product__media-item[data-media-id]').forEach((element) => {
            element.classList.remove('is-active');
            element.hidden = false;
            element.removeAttribute('aria-hidden');
          });
          activeMedia.classList.add('is-active');

          if (prepend && activeMedia.parentElement && activeMedia.parentElement.firstElementChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }
          return;
        }

        this.elements.viewer.querySelectorAll('.product__media-list > .product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        this.elements.viewer.querySelectorAll('.product__media-list > .product__media-item[data-media-id]').forEach((element) => {
          const active = element === activeMedia;
          element.hidden = !active;
          element.setAttribute('aria-hidden', String(!active));
        });

        if (prepend && activeMedia.parentElement) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail?.parentElement && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);

        const items = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-list > .product__media-item[data-media-id]')
        );
        const index = items.indexOf(activeMedia);
        if (this.elements.counter && index >= 0) {
          this.elements.counter.querySelector('.slider-counter--current').textContent = String(index + 1);
          this.elements.counter.querySelector('.slider-counter--total').textContent = String(items.length);
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        const position = activeThumbnail.dataset.mediaPosition;
        if (position && options.announce !== false) this.announceLiveRegion(activeMedia, position);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        const button = thumbnail.querySelector('button');
        if (!button) return;
        button.setAttribute('aria-current', true);

        if (typeof this.elements.thumbnails.isSlideVisible === 'function' && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion || !activeItem || !position) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          if (window.accessibilityStrings?.imageAvailable) {
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          }
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        const deferredMedia = activeItem?.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') deferredMedia.loadContent(false);
      }

      onPointerStart(event) {
        if (!event.isPrimary || event.button > 0) return;
        this.touchStart = { x: event.clientX, y: event.clientY, time: performance.now(), captured: false };
      }

      onPointerMove(event) {
        if (!this.touchStart || this.touchStart.captured || !event.isPrimary) return;
        const dx = Math.abs(event.clientX - this.touchStart.x);
        const dy = Math.abs(event.clientY - this.touchStart.y);
        if (dx > 8 && dx > dy * 1.25) {
          this.elements.viewer.setPointerCapture?.(event.pointerId);
          this.touchStart.captured = true;
        }
      }

      onPointerEnd(event) {
        if (!this.touchStart || !event.isPrimary) return;
        const dx = event.clientX - this.touchStart.x;
        const dy = event.clientY - this.touchStart.y;
        const velocity = Math.abs(dx) / Math.max(performance.now() - this.touchStart.time, 1);
        this.touchStart = null;
        if (Math.abs(dx) <= Math.abs(dy) * 1.25) return;
        const stage = this.elements.viewer.querySelector('.product__media-list');
        const items = Array.from(stage?.querySelectorAll(':scope > .product__media-item[data-media-id]') || []);
        const activeIndex = items.findIndex((item) => item.classList.contains('is-active'));
        if (items.length < 2 || activeIndex < 0 || (Math.abs(dx) < 48 && velocity < 0.35)) return;
        const nextIndex = (activeIndex + (dx < 0 ? 1 : -1) + items.length) % items.length;
        this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (Array.isArray(this.elements.viewer.sliderItems)) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }
    }
  );
}
